import { useTranslation } from 'react-i18next';
import { Button } from 'components/atoms';
import { LabeledStatus } from 'components/molecules';
import LabeledStatementConsistancy from 'components/molecules/labeledStatus/LabeledStatementConsistancy';
import Popup from 'components/molecules/popup';
import { Form } from 'components/organisms';
import { initialStatement } from 'components/organisms/forms/statements/ExchangeRatesForm';
import { useCountdown } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import { t } from 'i18next';
import { PiDownload, PiTrash } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  useCheckStatementConsistency,
  useDeleteStatement,
  useGetStatementDetail,
} from 'services/statements/index';
import { STATEMENT_STATUS } from 'services/statements/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { handleDownloadFile } from 'utils/global/linkToUrl.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const Statement = () => {
  const { t } = useTranslation();
  const i18nextPrefix = 'statements';
  const statementId = useParamId('statements');
  const { data, isLoading, refetch } = useGetStatementDetail(statementId);
  const navigate = useNavigate();
  const checkStatement = useCheckStatementConsistency();

  const { startCounter, isCountingDown, countdown } = useCountdown(60, false);

  const datas = data
    ? {
        ...data,
        defaultTotalValue: parseFloat(data.defaultTotalValue.toFixed(2)),
      }
    : initialStatement;

  const deleteStatementMutation = useDeleteStatement();

  const deleteStatement = async (id: string) => {
    await deleteStatementMutation.mutateAsync(id, {
      onSuccess: () => {
        navigate('/statements');
      },
    });
  };

  return (
    <Form.ExchangeRates
      i18nextPrefix={i18nextPrefix}
      initialValues={datas ?? initialStatement}
      topbarContent={
        data && (
          <>
            {data.statementStatus === STATEMENT_STATUS.done && (
              <>
                <Button
                  size='compact'
                  disabled={isLoading || isCountingDown}
                  isLoading={checkStatement.isPending}
                  onClick={async () =>
                    await checkStatement.mutateAsync(statementId, {
                      onSuccess: () => {
                        refetch();
                        startCounter();
                      },
                      onError: error => onErrorShowAllErrorsToast(error),
                    })
                  }>
                  <Button.Text
                    text={`${t('button.check-consistancy')} ${
                      isCountingDown ? `(${countdown}s)` : ''
                    }`}
                  />
                </Button>
                {data.consistency && (
                  <LabeledStatementConsistancy status={data.consistency} />
                )}
              </>
            )}
            <LabeledStatus.Statement status={data.statementStatus} />
            <div className='flex'>
              <Button
                onlyIcon
                isClear
                onClick={() => handleDownloadFile(data.statementFilePath)}>
                <Button.Description description={t('button.download')} />


                <Button.Icon icon={<PiDownload />} />
              </Button>
              <Popup.DeleteWarning
                name={`${getSettlementPeriodName(data.settlementPeriodName)} ${
                  data.salesChannelName
                } ${data.providerName}`}
                onRemove={() => {
                  deleteStatement(statementId);
                }}
                trigger={
                  <Button variant={BUTTON_VARIANT.remove} onlyIcon isClear>
                    <Button.Description description={t('button.remove')} />

                    <Button.Icon icon={<PiTrash />} />
                  </Button>
                }
              />
            </div>
          </>
        )
      }
      onSubmit={() => {}}
      tittle={data?.salesChannelName}
      status={{ displayMode: true, isLoading }}
    />
  );
};

export default Statement;
